import { McpServerConfigurationType } from "../enums";
export const PREDEFINED_MCP_SERVER_NAMES = [
  "Notion",
  "Linear",
  "Atlassian",
  "PostHog",
  "Amplitude",
  "Mixpanel",
] as const;

export type McpAppCatalogUrlOption = {
  readonly label: string;
  readonly url: string;
};

type McpAppCatalogEntryBase = {
  readonly configurationType: typeof McpServerConfigurationType.PRE_DEFINED;
  readonly description: string;
  readonly id: string;
  readonly name: (typeof PREDEFINED_MCP_SERVER_NAMES)[number];
};

export type McpAppCatalogEntry = McpAppCatalogEntryBase &
  (
    | {
        readonly setupPolicy: "instant";
        readonly urlOptions: readonly [McpAppCatalogUrlOption];
      }
    | {
        readonly setupPolicy: "chooseUrl";
        readonly urlOptions: readonly [
          McpAppCatalogUrlOption,
          McpAppCatalogUrlOption,
          ...McpAppCatalogUrlOption[],
        ];
      }
  );

export type McpAppCatalogSetupEntry = Exclude<
  McpAppCatalogEntry,
  { readonly setupPolicy: "instant" }
>;

export const catalogAppRequiresSetup = (
  entry: McpAppCatalogEntry,
): entry is McpAppCatalogSetupEntry => entry.setupPolicy !== "instant";

export const McpAppCatalog: readonly McpAppCatalogEntry[] = [
  {
    id: "notion",
    name: "Notion",
    description:
      "Connect Notion to the Hex Agent to create, edit, and search Notion documents.",
    configurationType: McpServerConfigurationType.PRE_DEFINED,
    setupPolicy: "instant",
    urlOptions: [{ label: "Notion", url: "https://mcp.notion.com/mcp" }],
  },
  {
    id: "linear",
    name: "Linear",
    description:
      "Connect Linear to the Hex Agent to find, create, and update Linear issues, projects, and comments.",
    configurationType: McpServerConfigurationType.PRE_DEFINED,
    setupPolicy: "instant",
    urlOptions: [{ label: "Linear", url: "https://mcp.linear.app/mcp" }],
  },
  {
    id: "atlassian",
    name: "Atlassian",
    description: "Search and manage Jira issues and Confluence content.",
    configurationType: McpServerConfigurationType.PRE_DEFINED,
    setupPolicy: "instant",
    urlOptions: [
      { label: "Atlassian Cloud", url: "https://mcp.atlassian.com/v2/mcp" },
    ],
  },
  {
    id: "posthog",
    name: "PostHog",
    description: "Query product analytics, feature flags, and experiments.",
    configurationType: McpServerConfigurationType.PRE_DEFINED,
    setupPolicy: "instant",
    urlOptions: [{ label: "PostHog", url: "https://mcp.posthog.com/mcp" }],
  },
  {
    id: "amplitude",
    name: "Amplitude",
    description: "Analyze product analytics, cohorts, and experiments.",
    configurationType: McpServerConfigurationType.PRE_DEFINED,
    setupPolicy: "chooseUrl",
    urlOptions: [
      { label: "US", url: "https://mcp.amplitude.com/mcp" },
      { label: "EU", url: "https://mcp.eu.amplitude.com/mcp" },
    ],
  },
  {
    id: "mixpanel",
    name: "Mixpanel",
    description: "Query events, funnels, and retention reports.",
    configurationType: McpServerConfigurationType.PRE_DEFINED,
    setupPolicy: "chooseUrl",
    urlOptions: [
      { label: "US", url: "https://mcp.mixpanel.com/mcp" },
      { label: "EU", url: "https://mcp-eu.mixpanel.com/mcp" },
      { label: "India", url: "https://mcp-in.mixpanel.com/mcp" },
    ],
  },
];
